import type { APIBookingDraft, APIBookingDraftUserRedeemableBooking } from '@/scopes/checkout/infrastructure/api/types';
import {
  type CheckoutBookingRedeem,
  type CheckoutBookingRedeemerService,
  type CheckoutPerRoomService,
  type ServiceMapper,
  ServiceType,
} from '../../../domain/types';
import { isNil, values } from 'ramda';
import type { AncillariesAddedAndRemovedEventProps } from '@/scopes/checkout/domain/analytics/types';

interface Dependencies {
  serviceName: string;
  serviceTypeMapper: string;
}

export const createBookingRedeemServiceMapper: (
  dependencies: Dependencies
) => ServiceMapper<CheckoutBookingRedeemerService> = (dependencies) => {
  const { serviceName, serviceTypeMapper } = dependencies;

  function map(bookingDraft: APIBookingDraft) {
    const { user } = bookingDraft;
    if (!user) {
      return null;
    }
    const selectedValue = mapSelectedValue(user?.redeemableBookings || []);
    return {
      id: 'BookingRedeemService',
      selectedValue,
      isSelected: !isNil(selectedValue),
      type: ServiceType.BookingRedeem,
      bookings: (user?.redeemableBookings || []).reduce(
        (acc2: Record<string, CheckoutBookingRedeem>, booking) => ({
          ...acc2,
          [booking.number]: mapRedeemableBooking(booking),
        }),
        {}
      ),
    };
  }

  function mapRedeemableBooking(redeemableBooking: APIBookingDraftUserRedeemableBooking) {
    if (!redeemableBooking) {
      return null;
    }
    return {
      bookingNumber: redeemableBooking.number.toString(),
      description: redeemableBooking.description,
      date: redeemableBooking.date,
      disabled: !redeemableBooking.isRedeemable,
      paidAmount: redeemableBooking.paidAmount.value,
      composition: redeemableBooking.composition,
      location: `${redeemableBooking.location.country}, ${redeemableBooking.location.region}, ${redeemableBooking.location.city}`,
      accoImageUrl: redeemableBooking.accoImageUrl,
    };
  }

  function getServiceName() {
    return serviceName;
  }

  function getServiceTypeMapper() {
    return serviceTypeMapper;
  }

  function mapSelectedValue(redeemableBookings: APIBookingDraftUserRedeemableBooking[]) {
    if (!redeemableBookings) {
      return null;
    }
    const selectedBooking = redeemableBookings.find((redeemableBooking) => redeemableBooking.selected);
    return selectedBooking?.number.toString() || null;
  }

  function mapSelection(service: CheckoutBookingRedeemerService) {
    return { value: service.selectedValue };
  }

  function mapAncillaryAddedAndRemovedToGA4Result(
    service: CheckoutBookingRedeemerService,
    previousService: CheckoutBookingRedeemerService
  ): {
    ancillaryAddMapped: AncillariesAddedAndRemovedEventProps[];
    ancillaryRemoveMapped: AncillariesAddedAndRemovedEventProps[];
  } {
    const ancillaryAddMapped = [];
    const ancillaryRemoveMapped = [];

    if (service?.selectedValue) {
      const newBooking = service?.bookings ? service?.bookings[service.selectedValue] : null;
      if (newBooking) {
        ancillaryAddMapped.push({
          id: newBooking.bookingNumber,
          name: newBooking.description,
          price: newBooking.paidAmount,
          category: 'BookingRedeemService',
          quantity: Number(newBooking.composition),
          serviceType: serviceTypeMapper,
          extraProperties: {},
        });
      }
    }
    if (previousService?.selectedValue) {
      const previousBooking = previousService?.bookings
        ? previousService?.bookings[previousService.selectedValue]
        : null;
      if (previousBooking) {
        ancillaryRemoveMapped.push({
          id: previousBooking.bookingNumber,
          name: previousBooking.description,
          price: previousBooking.paidAmount,
          category: 'BookingRedeemService',
          quantity: Number(previousBooking.composition),
          serviceType: serviceTypeMapper,
          extraProperties: {},
        });
      }
    }

    return {
      ancillaryAddMapped,
      ancillaryRemoveMapped,
    };
  }

  return {
    getServiceName,
    getServiceTypeMapper,
    map,
    mapSelection,
    mapAncillaryAddedAndRemovedToGA4Result,
  };
};
